import { useState, useCallback, useMemo } from 'react';
import useField, { UseFieldType, useFieldInitialState } from './useField';

export interface UseShareWithUsers {
  field: {
    userIds: string[];
  };
  action: {
    setUserIds: (ids: string[]) => void;
    resetField: () => void;
  };
}

export interface UseCalendarType {
  nameField: UseFieldType;
  color: string;
  handleColorChange: (hexColor: string) => void;
  shareWithUsers: UseShareWithUsers;
  googleCalendar: any;
  handleGoogleCalendarChange: (config: any) => void;
}

const useShareWithUsersInitial: UseShareWithUsers = {
  field: {
    userIds: [],
  },
  action: {
    setUserIds: () => {},
    resetField: () => {},
  },
};

export const useCalendarInitialState: UseCalendarType = {
  nameField: useFieldInitialState,
  color: '#ffffff',
  handleColorChange: () => {},
  shareWithUsers: useShareWithUsersInitial,
  googleCalendar: null,
  handleGoogleCalendarChange: () => {},
};

// Custom Hook para gestionar usuarios compartidos
const useShareWithUsersState = (): UseShareWithUsers => {
  const [userIds, setUserIds] = useState<string[]>([]);

  const resetField = useCallback(() => {
    setUserIds([]);
  }, []);

  const field = useMemo(
    () => ({
      userIds,
    }),
    [userIds],
  );

  const action = useMemo(
    () => ({
      setUserIds,
      resetField,
    }),
    [resetField],
  );

  return { field, action };
};

// Hook principal
export const useCalendar = (): UseCalendarType => {
  const nameField = useField('text', '');
  const [color, setColor] = useState('#ffffff');
  const [googleCalendar, setGoogleCalendar] = useState<any>(null);
  const shareWithUsers = useShareWithUsersState();

  const handleColorChange = useCallback((hexColor: string) => {
    setColor(hexColor);
  }, []);

  const handleGoogleCalendarChange = useCallback((config: any) => {
    setGoogleCalendar(config);
  }, []);

  return useMemo(
    () => ({
      nameField,
      color,
      handleColorChange,
      shareWithUsers,
      googleCalendar,
      handleGoogleCalendarChange,
    }),
    [
      nameField,
      color,
      handleColorChange,
      shareWithUsers,
      googleCalendar,
      handleGoogleCalendarChange,
    ],
  );
};

export default useCalendar;
